import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DataTable from '../../components/DataTable';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import { useCRUD } from '../../hooks/useCRUD';
import { medicineService, categoryService, brandService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import toast from 'react-hot-toast';

// ─── Constants ────────────────────────────────────────────────────────────────
const DRUG_FORMS = [
  'tablet','capsule','ampule','vial','injection','infusion','serum','syrup',
  'cream','ointment','gel','lotion','soap','pomad','solution',
  'drops','inhaler','spray','powder','sachet','other'
];
const STRENGTHS = ['250mg','500mg','1000mg','5ml','10ml','100ml','250mg/5ml','500mg/5ml'];
const PRESET_BASE_UNITS = ['strip','tablet','capsule','ml','g','piece','bottle','vial','ampule','sachet'];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MedicinesPage() {
  const crud = useCRUD(medicineService, 'Medicine');
  const [categories,    setCategories]   = useState([]);
  const [brands,        setBrands]       = useState([]);
  const [deleteTarget,  setDeleteTarget] = useState(null);
  const [deleting,      setDeleting]     = useState(false);
  const [filterStatus,  setFilterStatus] = useState('active');
  const [customBase, setCustomBase] = useState(false);

  const {
    register, handleSubmit, reset, watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      genericName:'', commercialName:'', category:'', brand:'',
      drugForm:'tablet', strength:'', customStrength:'',
      baseUnit:'strip', customBaseUnit:'',
      purchasePrice:'', sellingPrice:'',
      openingQty:'',
      manufacturer:'', barcode:'', description:'',
      storageInstructions:'', requiresPrescription:false, isActive:'true',
      salesUnits:[],
    }
  });


  const watchStrength  = watch('strength');

  // Load categories and brands
  useEffect(() => {
    categoryService.getAll({ limit:100 }).then(r => setCategories(r.data.data||[])).catch(()=>{});
    brandService.getAll({ limit:100 }).then(r => setBrands(r.data.data||[])).catch(()=>{});
  }, []);

  // Fetch medicines on filter/page/search change
  useEffect(() => {
    const params = {};
    if (filterStatus !== 'all') params.isActive = filterStatus === 'active' ? 'true' : 'false';
    crud.fetchData(params);
  }, [crud.page, crud.search, filterStatus]); // eslint-disable-line

  // Populate form on edit
  useEffect(() => {
    if (crud.editItem) {
      const med = crud.editItem;
      const isPresetBase = PRESET_BASE_UNITS.includes(med.baseUnit);
      setCustomBase(!isPresetBase);
      reset({
        genericName:          med.genericName    || '',
        commercialName:       med.commercialName || '',
        category:             med.category?._id  || med.category  || '',
        brand:                med.brand?._id     || med.brand     || '',
        drugForm:             med.drugForm       || 'tablet',
        strength:             STRENGTHS.includes(med.strength) ? med.strength : '',
        customStrength:       !STRENGTHS.includes(med.strength) ? (med.strength||'') : '',
        baseUnit:             isPresetBase ? med.baseUnit : 'strip',
        customBaseUnit:       !isPresetBase ? med.baseUnit : '',
          manufacturer:         med.manufacturer   || '',
        barcode:              med.barcode        || '',
        description:          med.description    || '',
        storageInstructions:  med.storageInstructions || '',
        requiresPrescription: med.requiresPrescription || false,
        isActive:             med.isActive !== false ? 'true' : 'false',
      });
    } else {
      setCustomBase(false);
      reset({
        genericName:'', commercialName:'', category:'', brand:'',
        drugForm:'tablet', strength:'', customStrength:'',
        baseUnit:'strip', customBaseUnit:'',
        manufacturer:'', barcode:'', description:'',
        storageInstructions:'', requiresPrescription:false, isActive:'true',
      });
    }
  }, [crud.editItem]); // eslint-disable-line


  const onSubmit = (data) => {
    const effectiveBase     = customBase ? (data.customBaseUnit||'strip') : data.baseUnit;
    const effectiveStrength = data.strength || data.customStrength || '';

    const payload = {
      genericName:         data.genericName,
      commercialName:      data.commercialName,
      category:            data.category   || undefined,
      brand:               data.brand      || undefined,
      drugForm:            data.drugForm,
      strength:            effectiveStrength,
      baseUnit:            effectiveBase,
      purchasePrice:       Number(data.purchasePrice),
      sellingPrice:        Number(data.sellingPrice),
      openingQty:          Number(data.openingQty) || 0,
      manufacturer:        data.manufacturer   || undefined,
      barcode:             data.barcode        || undefined,
      description:         data.description   || undefined,
      storageInstructions: data.storageInstructions || undefined,
      requiresPrescription:data.requiresPrescription || false,
      isActive:            data.isActive === 'true',

    };

    crud.handleSave(payload);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await medicineService.delete(deleteTarget._id);
      toast.success('Medicine permanently deleted (inventory records removed)');
      crud.fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally { setDeleting(false); setDeleteTarget(null); }
  };

  // ─── Table columns ───────────────────────────────────────────────────────────
  const columns = [
    { key:'commercialName', label:'Medicine', sortable:true,
      render:(v, row) => (
        <div>
          <p className="font-medium text-gray-900">{v}</p>
          <p className="text-xs text-gray-400">
            {row.genericName}
            {row.strength ? ` · ${row.strength}` : ''}
            {row.drugForm  ? ` · ${row.drugForm}`  : ''}
          </p>
        </div>
      )
    },
    { key:'category',  label:'Category',   render:(v) => v?.name || '-' },
    { key:'baseUnit',  label:'Base Unit',
      render:(v, row) => (
        <div className="text-xs">
          <span className="bg-gray-100 px-2 py-0.5 rounded capitalize font-medium">{v}</span>
          {row.salesUnits?.length > 0 && (
            <p className="text-gray-400 mt-0.5">{row.salesUnits.map(u=>`${u.unitName}=${u.conversionToBase}${v}`).join(' | ')}</p>
          )}
        </div>
      )
    },
    { key:'purchasePrice', label:'Buy Price',  render:(v) => formatCurrency(v) },
    { key:'sellingPrice',  label:'Sell Price', render:(v) => formatCurrency(v) },
    { key:'isActive', label:'Status',
      render:(v) => <Badge color={v?'green':'gray'}>{v?'Active':'Inactive'}</Badge>
    },
  ];

  return (
    <div>
      <Breadcrumb/>
      <PageHeader
        title="Medicines"
        subtitle="Medicine catalog with units and pricing"
        actions={<Button onClick={crud.openCreate}><Plus size={16}/> Add Medicine</Button>}
      />

      {/* ── Status Filter ── */}
      <div className="flex gap-2 mb-4">
        {[{k:'active',l:'Active'},{k:'inactive',l:'Inactive'},{k:'all',l:'All'}].map(f=>(
          <button key={f.k} onClick={()=>setFilterStatus(f.k)}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${filterStatus===f.k?'bg-blue-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {f.l}
          </button>
        ))}
      </div>

      {/* ── Desktop Table ── */}
      <div className="hidden md:block">
        <DataTable
          columns={columns} data={crud.data} loading={crud.loading}
          totalItems={crud.total} page={crud.page} pageSize={20}
          onPageChange={crud.setPage} onSearch={crud.setSearch}
          title="Medicines" exportFilename="medicines"
          actions={(row) => (
            <>
              <button onClick={()=>crud.openEdit(row)} title="Edit"
                className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600">
                <Pencil size={15}/>
              </button>
              <button onClick={()=>setDeleteTarget(row)} title="Delete"
                className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600">
                <Trash2 size={15}/>
              </button>
            </>
          )}
        />
      </div>

      {/* ── Mobile Cards ── */}
      <div className="md:hidden space-y-3">
        {crud.loading
          ? Array(4).fill(0).map((_,i)=><div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse"/>)
          : crud.data.map(row=>(
            <div key={row._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-900">{row.commercialName}</p>
                  <p className="text-xs text-gray-400">
                    {row.genericName}
                    {row.strength?` · ${row.strength}`:''}
                    <span className="capitalize"> · {row.drugForm}</span>
                  </p>
                </div>
                <div className="flex gap-1">
                  <button onClick={()=>crud.openEdit(row)} className="p-1.5 rounded bg-blue-50 text-blue-600"><Pencil size={14}/></button>
                  <button onClick={()=>setDeleteTarget(row)} className="p-1.5 rounded bg-red-50 text-red-600"><Trash2 size={14}/></button>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span>Buy: <strong>{formatCurrency(row.purchasePrice)}</strong></span>
                <span>Sell: <strong>{formatCurrency(row.sellingPrice)}</strong></span>
                <Badge color={row.isActive?'green':'gray'}>{row.isActive?'Active':'Inactive'}</Badge>
              </div>
            </div>
          ))
        }
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          Create / Edit Modal
      ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={crud.modalOpen} onClose={crud.closeModal}
        title={crud.editItem ? 'Edit Medicine' : 'Add Medicine'}
        size="xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

          {/* ── 1. Medicine Identity ── */}
          <section>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 pb-1 border-b border-gray-100">
              Medicine Identity
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <FormField label="Generic Name" required error={errors.genericName?.message}>
                <Input {...register('genericName',{required:'Required'})} placeholder="e.g. Amoxicillin"/>
              </FormField>

              <FormField label="Commercial Name" required error={errors.commercialName?.message}>
                <Input {...register('commercialName',{required:'Required'})} placeholder="e.g. Amoxil"/>
              </FormField>

              {/* Drug Form */}
              <FormField label="Drug Form" required>
                <Select {...register('drugForm')}>
                  {DRUG_FORMS.map(f=>(
                    <option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>
                  ))}
                </Select>
              </FormField>

              {/* Strength */}
              <FormField label="Strength">
                <div className="space-y-1">
                  <Select {...register('strength')}>
                    <option value="">Select or type below</option>
                    {STRENGTHS.map(s=><option key={s} value={s}>{s}</option>)}
                  </Select>
                  <Input
                    {...register('customStrength')}
                    placeholder="Custom: e.g. 750mg, 250mg/5ml"
                    className="text-xs"
                  />
                  {(watchStrength || watch('customStrength')) && (
                    <p className="text-xs text-blue-600">
                      Strength: <strong>{watch('customStrength') || watchStrength}</strong>
                    </p>
                  )}
                </div>
              </FormField>

              {/* Category */}
              <FormField label="Category" required error={errors.category?.message}>
                <Select {...register('category',{required:'Required'})}>
                  <option value="">Select category</option>
                  {categories.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                </Select>
              </FormField>

              {/* Brand */}
              <FormField label="Brand">
                <Select {...register('brand')}>
                  <option value="">No brand (optional)</option>
                  {brands.map(b=><option key={b._id} value={b._id}>{b.name}</option>)}
                </Select>
              </FormField>

              <FormField label="Manufacturer">
                <Input {...register('manufacturer')} placeholder="e.g. GSK"/>
              </FormField>

              <FormField label="Barcode">
                <Input {...register('barcode')} placeholder="Optional"/>
              </FormField>

            </div>
          </section>

          {/* ── 2. Note about pricing ── */}
          <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
            <Info size={15} className="flex-shrink-0"/>
            <span>Base unit, pricing and opening stock are set in <strong>Inventory → Stock In</strong> after saving this medicine.</span>
          </div>

          {/* ── 4. Extra Info ── */}
          <section>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 pb-1 border-b border-gray-100">
              Additional Info
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Storage Instructions">
                <Input {...register('storageInstructions')} placeholder="e.g. Store below 25°C"/>
              </FormField>
              <FormField label="Status">
                <Select {...register('isActive')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </FormField>
            </div>
            <div className="mt-4">
              <FormField label="Description / Notes">
                <textarea
                  {...register('description')} rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Optional notes"
                />
              </FormField>
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-3">
              <input type="checkbox" {...register('requiresPrescription')} className="rounded w-4 h-4"/>
              <span className="text-sm text-gray-700">Requires Prescription (Rx)</span>
            </label>
          </section>

          {/* ── Submit ── */}
          <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
            <Button variant="secondary" type="button" onClick={crud.closeModal}>Cancel</Button>
            <Button type="submit" loading={crud.saving}>
              {crud.editItem ? 'Update Medicine' : 'Save Medicine'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Delete Confirm ── */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={()=>setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Permanently Delete Medicine"
        message={`Delete "${deleteTarget?.commercialName}"?\n\nThis will permanently remove the medicine AND all its inventory records from the database. This action CANNOT be undone.`}
        confirmLabel="Delete Permanently"
      />
    </div>
  );
}
